import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REVENUE_OPTIONS = [
  "Pre-revenue / Bootstrapping",
  "$100K to $500K ARR",
  "$500K to $1M ARR",
  "$1M to $5M ARR",
  "$5M+ ARR",
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    linkedin: "",
    company: "",
    revenue_range: "",
    gap: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.gap) {
      setError("Please fill in your name, email, and the biggest gap.");
      return;
    }
    try {
      setStatus("sending");
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", linkedin: "", company: "", revenue_range: "", gap: "" });
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
    }
  };

  return (
    <section className="section" id="qualify">
      <div className="container-x">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          {/* Left intro */}
          <Reveal x={-30} y={0}>
            <div>
            <div className="font-script" style={{ fontSize: 22, color: "var(--agero-orange)" }}>
              (Quick intro)
            </div>
            <h2
              className="font-display mt-3"
              style={{
                fontSize: "clamp(40px, 5.5vw, 84px)",
                letterSpacing: "-0.05em",
                lineHeight: 0.98,
              }}
            >
              Prefer to write
              <br />
              <span className="gradient-orange-text">a few lines first?</span>
            </h2>
            <p className="mt-6 text-neutral-700 text-[17px] leading-relaxed max-w-md">
              Send a quick note about where LinkedIn fits in your GTM. If
              there&apos;s a real fit, I&apos;ll reply with available call
              slots within 24 hours.
            </p>
            <ul className="mt-6 space-y-3 text-neutral-700 text-[15px]">
              <li className="flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: "var(--agero-orange)" }}
                />
                No newsletter. No automated sequences.
              </li>
              <li className="flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: "var(--agero-orange)" }}
                />
                Reviewed personally, replied to personally.
              </li>
              <li className="flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: "var(--agero-orange)" }}
                />
                Honest answer if we&apos;re not the right fit.
              </li>
            </ul>
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal x={30} y={0} delay={120}>
            <form
              onSubmit={submit}
              className="rounded-[28px] p-7 md:p-9 bg-white border border-neutral-200/70 shadow-sm"
            >
            {status === "success" ? (
              <div className="flex flex-col items-center text-center py-10">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                  style={{ background: "rgba(255,51,1,0.12)", color: "var(--agero-orange)" }}
                >
                  <CheckCircle2 size={26} />
                </div>
                <h3
                  className="font-display"
                  style={{ fontSize: 32, letterSpacing: "-0.03em" }}
                >
                  Got it. Talk soon.
                </h3>
                <p className="mt-3 text-neutral-600 max-w-sm">
                  Your note is in. You&apos;ll hear back within 24 hours,
                  usually faster.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-[14px] font-medium"
                  style={{ color: "var(--agero-orange)" }}
                >
                  Send another &rarr;
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-medium text-neutral-600 mb-2">
                      Your name
                    </label>
                    <input
                      value={form.name}
                      onChange={onChange("name")}
                      placeholder="Jane Doe"
                      className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-neutral-600 mb-2">
                      Work email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="jane@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-[13px] font-medium text-neutral-600 mb-2">
                    Your LinkedIn profile (personal)
                  </label>
                  <input
                    type="url"
                    value={form.linkedin}
                    onChange={onChange("linkedin")}
                    placeholder="https://linkedin.com/in/yourname"
                    className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-[13px] font-medium text-neutral-600 mb-2">
                    Company / website (optional)
                  </label>
                  <input
                    value={form.company}
                    onChange={onChange("company")}
                    placeholder="acme.com"
                    className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-[13px] font-medium text-neutral-600 mb-2">
                    Current revenue range
                  </label>
                  <select
                    value={form.revenue_range}
                    onChange={onChange("revenue_range")}
                    className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition"
                  >
                    <option value="">Select a range</option>
                    {REVENUE_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4">
                  <label className="block text-[13px] font-medium text-neutral-600 mb-2">
                    What&apos;s the biggest LinkedIn gap right now?
                  </label>
                  <textarea
                    value={form.gap}
                    onChange={onChange("gap")}
                    rows={4}
                    placeholder="e.g. We post often but never convert. We don't have lead magnets. Outreach is manual..."
                    className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 focus:outline-none focus:border-[color:var(--agero-orange)] focus:bg-white transition resize-none"
                  />
                </div>

                {error && (
                  <div
                    className="mt-3 text-[13px]"
                    style={{ color: "var(--agero-orange)" }}
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium transition disabled:opacity-60"
                  style={{ background: "#0A0A0A", color: "#fff" }}
                >
                  {status === "sending" ? "Sending..." : "Send the note"}
                  <ArrowRight size={16} />
                </button>

                <p className="mt-4 text-[12.5px] text-neutral-500 leading-relaxed">
                  We won&apos;t share your details. Replies come from MB
                  directly, usually within 24 hours.
                </p>
              </>
            )}
          </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
